import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, KeyRound, Link2Off, Plus, UserX, Users, type LucideIcon } from "lucide-react";

import { ParentDetails } from "../components/parents/ParentDetails";
import { CredentialsCard, LinkChildrenForm, ParentForm } from "../components/parents/ParentForms";
import { LearnersWithoutParentPanel, ParentsRegister } from "../components/parents/ParentsPanels";
import type { ParentView } from "../components/parents/parentsLogic";
import { Notice, type NoticeState } from "../components/register/RecordParts";
import { studentService, type LearnerWithoutParent, type Parent } from "../services/studentService";
import { apiErrorMessage } from "../utils/apiError";

type Tab = "parents" | "learners";
type Dialog = { kind: "new"; learner?: LearnerWithoutParent } | { kind: "edit"; parent: Parent } | { kind: "link"; parent: Parent } | null;

/**
 * Parents and guardians: their family portal logins and the children they are
 * linked to, the learners nobody is on record for, and the login help families
 * ask the office for (a forgotten password, a login to turn off).
 */
export default function ParentsGuardians() {
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const tab: Tab = params.get("tab") === "learners" ? "learners" : "parents";
  const view = (params.get("view") as ParentView | null) ?? undefined;
  const go = (next: Tab, nextView?: ParentView) => {
    const query: Record<string, string> = {};
    if (next !== "parents") query.tab = next;
    if (nextView) query.view = nextView;
    setParams(query, { replace: true });
  };
  const [notice, setNotice] = useState<NoticeState>(null);
  const [credentials, setCredentials] = useState<{ title: string; username: string; password: string } | null>(null);
  const [viewing, setViewing] = useState<Parent | null>(null);
  const [dialog, setDialog] = useState<Dialog>(null);

  const summary = useQuery({ queryKey: ["parents", "summary"], queryFn: studentService.getParentSummary });
  const classes = useQuery({ queryKey: ["class-streams", "all"], queryFn: () => studentService.getClasses({ ordering: "level" }) });
  const learners = useQuery({ queryKey: ["parents", "learners-without-parent"], queryFn: studentService.getLearnersWithoutParent, enabled: tab === "learners" });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["parents"] });
  const done = (text: string) => { setNotice({ tone: "success", text }); refresh(); };

  const save = useMutation({
    mutationFn: async ({ id, data, learner }: { id?: number; data: Record<string, unknown>; learner?: LearnerWithoutParent }) => {
      const saved = id ? await studentService.updateParent(id, data) : await studentService.createParent(data);
      // Added from a learner with nobody on record: link them straight away, as the primary contact.
      return learner ? studentService.linkParentChildren(saved.id, { student_ids: [learner.id], primary_student_ids: [learner.id] }) : saved;
    },
    onSuccess: (saved, { id, data, learner }) => {
      setDialog(null);
      if (!id) setCredentials({ title: `${saved.full_name}'s login is ready`, username: String(data.username), password: String(data.password) });
      done(id ? `${saved.full_name} saved.` : learner ? `${saved.full_name} added and linked to ${learner.full_name}.` : `${saved.full_name} added. Link their children next.`);
      if (id && viewing?.id === id) setViewing(saved);
      if (!id && !learner) setDialog({ kind: "link", parent: saved });
    },
  });
  const link = useMutation({
    mutationFn: ({ parent, student_ids, primary_student_ids }: { parent: Parent; student_ids: number[]; primary_student_ids: number[] }) => studentService.linkParentChildren(parent.id, { student_ids, primary_student_ids }),
    onSuccess: (saved) => {
      setDialog(null);
      if (viewing?.id === saved.id) setViewing(saved);
      done(`${saved.full_name} is linked to ${saved.children_data?.length ?? 0} ${saved.children_data?.length === 1 ? "child" : "children"}.`);
    },
    onError: (error) => setNotice({ tone: "error", text: apiErrorMessage(error, "The link could not be saved.") }),
  });

  const s = summary.data;
  const dash = (value?: number) => (summary.isLoading || value === undefined ? "–" : value.toLocaleString());
  const tiles: { label: string; value: string; note: string; go: () => void; icon: LucideIcon; alert?: boolean }[] = [
    { label: "Parents and guardians", value: dash(s?.total), note: s ? `${s.total - s.unlinked} linked to a child` : "", go: () => go("parents"), icon: Users },
    { label: "Learners with no parent", value: dash(s?.learners_without_parent), note: s?.learners_without_parent ? "Nobody on record to call" : "Every learner has one", go: () => go("learners"), icon: AlertTriangle, alert: Boolean(s?.learners_without_parent) },
    { label: "Parents with no child linked", value: dash(s?.unlinked), note: "They see nothing in the portal", go: () => go("parents", "unlinked"), icon: Link2Off, alert: Boolean(s?.unlinked) },
    { label: "Never signed in", value: dash(s?.never_signed_in), note: s?.inactive ? `${s.inactive} logins turned off` : "No logins turned off", go: () => go("parents", "never"), icon: s?.inactive ? UserX : KeyRound },
  ];
  const addButton = <button type="button" className="btn btn-primary h-9 gap-1.5 text-sm" onClick={() => { save.reset(); setDialog({ kind: "new" }); }}><Plus className="h-4 w-4" /> Add parent</button>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Parents & Guardians</h1>
        <p className="mt-1 text-sm text-gray-500">Family portal logins and the children each parent is linked to. Link every learner to at least one parent, so the school has someone to call and the family can see reports and fees.</p>
      </div>

      {credentials && <CredentialsCard {...credentials} onDismiss={() => setCredentials(null)} />}
      <Notice notice={notice} onDismiss={() => setNotice(null)} />

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {tiles.map((tile) => (
          <button key={tile.label} type="button" onClick={tile.go} className="flex items-center gap-4 rounded-xl border border-gray-200 bg-white p-4 text-left transition-colors hover:border-primary-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500">
            <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${tile.alert ? "bg-amber-50 text-amber-700" : "bg-primary-50 text-primary-700"}`}>
              <tile.icon className="h-5 w-5" />
            </span>
            <span className="min-w-0">
              <span className="block text-xs font-medium text-gray-500">{tile.label}</span>
              <span className={`block truncate text-xl font-semibold tabular-nums ${tile.alert ? "text-amber-800" : "text-gray-900"}`}>{tile.value}</span>
              <span className="block truncate text-xs text-gray-500">{tile.note}</span>
            </span>
          </button>
        ))}
      </div>

      <nav role="tablist" aria-label="Parents sections" className="-mb-px flex gap-x-6 overflow-x-auto border-b border-gray-200">
        {([["parents", "Parents and guardians", Users, 0], ["learners", "Learners with no parent", AlertTriangle, s?.learners_without_parent ?? 0]] as const).map(([id, label, Icon, badge]) => (
          <button key={id} type="button" role="tab" aria-selected={tab === id} onClick={() => go(id)} className={`inline-flex shrink-0 items-center gap-2 border-b-2 px-1 py-2.5 text-sm font-medium ${tab === id ? "border-primary-600 text-primary-700" : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"}`}>
            <Icon className="h-4 w-4" /> {label}
            {badge ? <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-xs tabular-nums text-amber-800">{badge}</span> : null}
          </button>
        ))}
      </nav>

      {tab === "parents" && <ParentsRegister key={view ?? "all"} summary={s} classes={classes.data?.results ?? []} initialView={view} actions={addButton} onOpen={setViewing} />}
      {tab === "learners" && (
        <LearnersWithoutParentPanel
          rows={learners.data}
          isLoading={learners.isLoading}
          isError={learners.isError}
          onRetry={() => learners.refetch()}
          onAddParent={(learner) => { save.reset(); setDialog({ kind: "new", learner }); }}
          onLinkExisting={(learner, parent) => link.mutate({
            parent,
            student_ids: [...(parent.children_data ?? []).map((child) => child.id), learner.id],
            primary_student_ids: [...(parent.children_data ?? []).filter((child) => child.is_primary).map((child) => child.id), learner.id],
          })}
        />
      )}

      {viewing && !dialog && (
        <ParentDetails
          key={viewing.id}
          parent={viewing}
          onClose={() => setViewing(null)}
          onEdit={() => { save.reset(); setDialog({ kind: "edit", parent: viewing }); }}
          onLink={() => { link.reset(); setDialog({ kind: "link", parent: viewing }); }}
          onChanged={(text) => { done(text); studentService.getParent(viewing.id).then(setViewing).catch(() => undefined); }}
          onCredentials={(username, password) => setCredentials({ title: `New temporary password for ${viewing.full_name}`, username, password })}
          onDeleted={(text) => { setViewing(null); done(text); }}
        />
      )}
      {dialog?.kind === "new" && <ParentForm forLearner={dialog.learner} busy={save.isPending} error={save.isError ? apiErrorMessage(save.error, "The parent could not be added.") : undefined} onSave={(data) => save.mutate({ data, learner: dialog.learner })} onClose={() => setDialog(null)} />}
      {dialog?.kind === "edit" && <ParentForm record={dialog.parent} busy={save.isPending} error={save.isError ? apiErrorMessage(save.error, "The parent could not be saved.") : undefined} onSave={(data) => save.mutate({ id: dialog.parent.id, data })} onClose={() => setDialog(null)} />}
      {dialog?.kind === "link" && <LinkChildrenForm parent={dialog.parent} busy={link.isPending} error={link.isError ? apiErrorMessage(link.error, "The links could not be saved.") : undefined} onSave={(data) => link.mutate({ parent: dialog.parent, ...data })} onClose={() => setDialog(null)} />}
    </div>
  );
}
